import { useMemo } from 'react';
import { parsePlacement } from '@chessmate/game-engine';
import type { PieceChar } from '@chessmate/protocol';

const FILES = 'abcdefgh';

const PIECE_GLYPH: Record<string, string> = {
  // Use the same short labels the move list uses so the minimap and the
  // sidebar read as the same game.
  p: '♟',
  n: '♞',
  b: '♝',
  r: '♜',
  q: '♛',
  k: '♚',
  // Checkers uses m / x for man / king.
  m: '○',
  x: '✕',
};

function pieceLabel(char: PieceChar): string {
  return PIECE_GLYPH[char.toLowerCase()] ?? char;
}

/**
 * 2D DOM-based 8×8 minimap that mirrors the position. Rendered absolutely
 * inside the board section, in the bottom-right corner. Toggleable via the
 * `showMinimap` flag in `useThemeStore`.
 *
 * The board's own `<ChessBoard>` is the source of truth for the visual state;
 * the minimap is a secondary readout for the player who wants to see the
 * whole position at a glance without orbiting the camera. Default off so the
 * board section stays clean.
 */
export function Minimap({ fen }: { fen: string }) {
  const cells = useMemo(() => parsePlacement(fen).slice(), [fen]);
  return (
    <div
      className="pointer-events-none absolute bottom-2 right-2 grid grid-cols-8 overflow-hidden rounded-xl border border-sun/30 bg-cream/90 p-1 shadow-md backdrop-blur"
      data-testid="minimap"
      aria-label="Position minimap"
    >
      {cells.map((char, i) => {
        const dark = (Math.floor(i / 8) + (i % 8)) % 2 === 1;
        const square = `${FILES[i % 8]}${8 - Math.floor(i / 8)}`;
        return (
          <div
            key={square}
            data-testid={`minimap-cell-${square}`}
            data-piece={char ?? ''}
            className={`flex h-3 w-3 items-center justify-center text-[0.5rem] leading-none ${
              dark ? 'bg-ink/10' : 'bg-cream'
            } ${char && char === char.toUpperCase() ? 'text-ink' : 'text-ink/60'}`}
          >
            {char ? pieceLabel(char) : ''}
          </div>
        );
      })}
    </div>
  );
}
